import "./Home.css";
import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { UserContext } from "../App.js";
import { supabase } from "../supabaseClient";
import Skeleton from "./Skeleton";

import p1_1 from "../Assets/Images/p1.1.jpg";
import p2_1 from "../Assets/Images/p2.1.jpg";

const Portfolio = () => {
  const { setState } = useContext(UserContext);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // CRUD form state
  const [form, setForm] = useState({ title: "", description: "", url: "", image_url: "" });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  useEffect(() => {
    let mounted = true;
    async function fetchProjects() {
      setLoading(true);
      setError(null);
      try {
        const { data, error } = await supabase
          .from("projects_data")
          .select("*")
          .order("created_at", { ascending: false });
        if (error) throw error;
        if (mounted) {
          const list = data || [];
          // Resolve storage image paths to usable URLs
          await Promise.all(list.map(async (p) => {
            let raw = p.images || p.image || p.image_url || p.imageUrl || p.img;
            if (!raw) {
              p._imageUrl = '';
              return;
            }

            // If it's already a full URL, use it
            if (typeof raw === 'string' && raw.startsWith('http')) {
              p._imageUrl = raw;
              return;
            }

            // Normalize storage path: remove leading slashes and optional bucket prefix
            let path = (typeof raw === 'string' ? raw : String(raw)).trim();
            path = path.replace(/^\/+/, ''); // remove leading /
            // If someone stored 'images/filename', strip the 'images/' prefix because bucket is 'images'
            path = path.replace(/^images\//, '');

            // Try getPublicUrl first
            try {
              const pub = supabase.storage.from('images').getPublicUrl(path);
              const publicUrl = pub?.data?.publicUrl || pub?.data?.publicURL || pub?.publicURL || pub?.publicUrl;
              if (publicUrl) {
                p._imageUrl = publicUrl;
                console.debug('Resolved publicUrl for', path, publicUrl);
                return;
              }
            } catch (e) {
              console.debug('getPublicUrl failed for', path, e);
            }

            // Try signed URL (for private buckets) — may require service role in some setups
            try {
              const ttl = 60 * 60; // 1 hour
              const signed = await supabase.storage.from('images').createSignedUrl(path, ttl);
              const signedUrl = signed?.data?.signedUrl || signed?.signedURL || signed?.data?.signedURL;
              if (signedUrl) {
                p._imageUrl = signedUrl;
                console.debug('Resolved signedUrl for', path, signedUrl);
                return;
              }
            } catch (e) {
              console.error('Error creating signed url for', path, e);
            }

            p._imageUrl = '';
          }));

          setProjects(list);
        }
      } catch (err) {
        console.error("Error fetching projects:", err.message || err);
        if (mounted) setError(err.message || String(err));
      } finally {
        if (mounted) setLoading(false);
      }
    }

    fetchProjects();
    return () => {
      mounted = false;
    };
  }, []);

  // If DB has no projects, fall back to static items
  const displayItems = projects.length > 0 ? projects : [
    { id: "static-1", title: "Static App 1", image: p1_1, image_url: null, url: "#", description: "" },
    { id: "static-2", title: "Static Web 3", image: p2_1, image_url: null, url: "#", description: "" }
  ];

  // CRUD handlers
  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }

  async function handleCreate(e) {
    e.preventDefault();
    if (!form.title.trim()) return alert('Title is required');
    setSaving(true);
    try {
      const { data, error } = await supabase.from('projects_data').insert([form]).select();
      if (error) throw error;
      // Prepend new project
      setProjects(prev => [data[0], ...prev]);
      setForm({ title: '', description: '', url: '', image_url: '' });
    } catch (err) {
      console.error('Create error', err);
      alert(err.message || String(err));
    } finally {
      setSaving(false);
    }
  }

  function startEdit(p) {
    setEditingId(p.id);
    setForm({ title: p.title || '', description: p.description || '', url: p.url || '', image_url: p.image_url || '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function handleUpdate(e) {
    e.preventDefault();
    if (!editingId) return;
    setSaving(true);
    try {
      const patch = { ...form };
      const { data, error } = await supabase.from('projects_data').update(patch).eq('id', editingId).select();
      if (error) throw error;
      setProjects(prev => prev.map(p => (p.id === editingId ? data[0] : p)));
      setEditingId(null);
      setForm({ title: '', description: '', url: '', image_url: '' });
    } catch (err) {
      console.error('Update error', err);
      alert(err.message || String(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this project?')) return;
    try {
      const { error } = await supabase.from('projects_data').delete().eq('id', id);
      if (error) throw error;
      setProjects(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      console.error('Delete error', err);
      alert(err.message || String(err));
    }
  }

  function cancelEdit() {
    setEditingId(null);
    setForm({ title: '', description: '', url: '', image_url: '' });
  }

  // Debug helper: call Supabase and show raw response
  async function debugFetch() {
    try {
      const { data, error, status } = await supabase.from('projects_data').select('*').limit(20);
      console.log('Supabase debugFetch response:', { status, data, error });
      if (error) {
        alert('Supabase error: ' + (error.message || JSON.stringify(error)));
      } else {
        alert('Fetched ' + (data ? data.length : 0) + ' rows. See console for details.');
      }
    } catch (err) {
      console.error('debugFetch exception', err);
      alert('Exception: ' + (err.message || String(err)));
    }
  }

  return (
    <>
      <section id="portfolio" className="portfolio section-bg">
        <div className="container">
          <div className="section-title">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <h2 style={{ margin: 0 }}>Projects</h2>
            </div>
            <p>{error ? `Error: ${error}` : ''}</p>
          </div>

          <div
            className="row portfolio-container"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            {loading && (
              <>
                {[1, 2, 3].map((n) => (
                  <div key={n} className="col-lg-4 col-md-6 portfolio-item">
                    <div className="portfolio-wrap" style={{ background: 'var(--bg-color)', border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'hidden' }}>
                      <Skeleton height="200px" />
                      <div className="portfolio-info mt-2" style={{ padding: '15px' }}>
                        <Skeleton height="24px" width="70%" style={{ marginBottom: '10px' }} />
                        <Skeleton height="16px" width="100%" />
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            {!loading && displayItems.map((p, idx) => {
              // Normalize project fields (your table uses `name`, `description`, `images`, `link`)
              const projName = p.name || p.title || p.name;
              const projDesc = p.description || p.desc || p.summary || '';

              // Normalize images: could be a single URL, comma-separated, or JSON array
              let imageUrl = '';
              const rawImages = p.images || p.image || p.image_url || p.imageUrl || p.img;
              if (rawImages) {
                if (typeof rawImages === 'string') {
                  const trimmed = rawImages.trim();
                  if (trimmed.startsWith('[')) {
                    try {
                      const arr = JSON.parse(trimmed);
                      imageUrl = arr && arr.length ? arr[0] : '';
                    } catch (e) {
                      // fallback to comma split
                      const parts = trimmed.split(',').map(s => s.trim()).filter(Boolean);
                      imageUrl = parts[0] || '';
                    }
                  } else if (trimmed.includes(',')) {
                    imageUrl = trimmed.split(',').map(s => s.trim()).filter(Boolean)[0] || '';
                  } else {
                    imageUrl = trimmed;
                  }
                } else if (Array.isArray(rawImages)) {
                  imageUrl = rawImages.length ? rawImages[0] : '';
                } else {
                  imageUrl = String(rawImages);
                }
              }

              // Fallback to bundled images for static items
              const finalImage = imageUrl || p.image || p.img || p1_1;

              return (
                <div key={p.id || idx} className="col-lg-4 col-md-6 portfolio-item" data-aos="zoom-in" data-aos-delay={idx * 100}>
                  <div className="portfolio-wrap">
                    <img src={finalImage} className="img-fluid" alt={projName || 'project'} />
                    <div className="portfolio-links">
                      <a
                        href={finalImage || '#'}
                        data-gallery="portfolioGallery"
                        className="portfolio-lightbox"
                        title={projName || ''}
                      >
                        <i className="bx bx-plus"></i>
                      </a>
                      <Link
                        to={`/project/${p.id}`}
                        title="More Details"
                      >
                        <i className="bx bx-link"></i>
                      </Link>
                    </div>
                    <div className="portfolio-info mt-2">
                      <h4>{projName}</h4>
                      <p>{projDesc}</p>
                    
                    </div>
                  </div>
                </div>
              );
            })}

          </div>
        </div>
      </section>
    </>
  );
};

export default Portfolio;
